/**
 * Chat Interface Component
 * Handles natural language query input and displays conversation history
 */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { translateQuery, getExampleQueries, conversationService } from '../services/nlpService';
import { executeGISQuery, findNearbyTracts, findNearbyTractsMultiCenter } from '../services/gisService';
import dossierService from '../services/dossierService';
import apiClient from '../services/apiClient';
import { getTenantConfig } from '../config/tenantConfig';
import { Send, Loader, AlertCircle, Lightbulb, Save, FolderOpen, Download, Upload, Trash2, X, Sparkles, ArrowRight } from 'lucide-react';

// Get tenant config
const tenantConfig = getTenantConfig();

// Simple markdown renderer for results (safe - no dangerouslySetInnerHTML)
function FormattedText({ text }) {
  if (!text) return null;

  // Split by newlines and process each line
  const lines = text.split('\n');

  return (
    <span className="formatted-text">
      {lines.map((line, lineIdx) => {
        // Process bold markers **text**
        const parts = [];
        let remaining = line;
        let partIdx = 0;

        while (remaining.length > 0) {
          const boldStart = remaining.indexOf('**');
          if (boldStart === -1) {
            parts.push(<span key={partIdx++}>{remaining}</span>);
            break;
          }

          // Text before bold
          if (boldStart > 0) {
            parts.push(<span key={partIdx++}>{remaining.substring(0, boldStart)}</span>);
          }

          // Find end of bold
          const boldEnd = remaining.indexOf('**', boldStart + 2);
          if (boldEnd === -1) {
            parts.push(<span key={partIdx++}>{remaining.substring(boldStart)}</span>);
            break;
          }

          // Bold text
          const boldText = remaining.substring(boldStart + 2, boldEnd);
          parts.push(<strong key={partIdx++}>{boldText}</strong>);
          remaining = remaining.substring(boldEnd + 2);
        }

        // Handle list items (lines starting with "- ")
        const isBullet = line.trimStart().startsWith('- ');

        return (
          <React.Fragment key={lineIdx}>
            {lineIdx > 0 && <br />}
            {isBullet ? <span>• {parts.length > 0 ? parts : line.replace(/^[\s]*- /, '')}</span> : parts}
          </React.Fragment>
        );
      })}
    </span>
  );
}

// Helper function for authenticated fetch requests
async function authFetch(url, options = {}) {
  const headers = { ...options.headers };
  try {
    const token = await apiClient.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) { /* proceed without auth */ }
  return fetch(url, { ...options, headers });
}

const ChatInterface = forwardRef(function ChatInterface({ onQueryResults, onError, onAnalytics, onClearAnalytics, onSymbologyChange, currentResults, queryText, analyticsData }, ref) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [lastResults, setLastResults] = useState(null);
  const lastResultsRef = useRef(null); // Ref for immediate access (React state is async)
  const [analyticsActive, setAnalyticsActive] = useState(false);
  const [lastAnalyticsQuery, setLastAnalyticsQuery] = useState(null);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);
  const [pendingInteraction, setPendingInteraction] = useState(null); // For smart follow-up questions
  const messagesEndRef = useRef(null);
  const exampleQueries = getExampleQueries();

  // Smart interactive queries - when clicked, ask for user input via chips
  // Templates use "filter to" / "only" keywords to trigger filterResults instead of new queries
  const smartQueries = [
    {
      id: 'filter-county',
      label: '🏛️ Filter by County',
      question: 'Which county would you like to filter to?',
      options: ['Reeves', 'Howard', 'Midland', 'Pecos', 'Loving', 'Martin', 'Culberson', 'Ward'],
      template: 'Filter to {value} County only'
    },
    {
      id: 'filter-operator',
      label: '🏢 Filter by Operator',
      question: 'Which operator would you like to filter to?',
      options: ['Diamondback', 'EOG', 'ConocoPhillips', 'Mewbourne', 'Pioneer', 'Devon', 'Chevron', 'Occidental'],
      template: 'Only {value} wells'
    },
    {
      id: 'filter-status',
      label: '📊 Filter by Well Status',
      question: 'What well status would you like to filter to?',
      options: ['Producing', 'DUC', 'Drilling', 'Permitted', 'Shut-in'],
      template: 'Only {value} wells'
    },
    {
      id: 'filter-formation',
      label: '⛏️ Filter by Formation',
      question: 'Which formation/interval?',
      options: ['Wolfcamp', 'Bone Spring', 'Spraberry', 'Delaware', 'Avalon'],
      template: 'Only {value} wells'
    },
    {
      id: 'filter-acreage',
      label: '📏 Filter by Acreage',
      question: 'What acreage threshold?',
      options: ['Over 100 acres', 'Over 200 acres', 'Over 500 acres', 'Under 50 acres'],
      template: 'Filter to tracts {value}'
    },
    {
      id: 'filter-contract',
      label: '📝 Filter by Contract Type',
      question: 'What type of contract?',
      options: ['Lease', 'Easement', 'Surface', 'Mineral', 'ROW'],
      template: 'Only {value} contracts'
    }
  ];

  // Saved Analysis (Dossier) state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [analysisName, setAnalysisName] = useState('');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const fileInputRef = useRef(null);

  // Expose sendQuery method to parent components via ref
  useImperativeHandle(ref, () => ({
    sendQuery: (query) => {
      console.log('📤 External query received:', query);
      handleSubmit(null, query);
    }
  }));

  // Load saved analyses on mount
  useEffect(() => {
    async function loadAnalyses() {
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
    loadAnalyses();
  }, []);

  // Track analytics data for saving
  useEffect(() => {
    // This effect captures analytics data when it changes
    // We'll use a custom event or callback approach
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if there are messages)
  useEffect(() => {
    // Don't auto-scroll on welcome screen - only scroll when there are actual messages
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-refresh analytics when lastResults changes (if analytics mode is active)
  useEffect(() => {
    async function refreshAnalytics() {
      if (analyticsActive && lastAnalyticsQuery) {
        // If results are empty, clear analytics
        if (!lastResults || lastResults.length === 0) {
          console.log('🔄 Query returned 0 results - clearing analytics');
          if (onClearAnalytics) {
            onClearAnalytics();
          }
          return;
        }

        // Otherwise, refresh analytics with new data
        console.log('🔄 Auto-refreshing analytics for updated results. Count:', lastResults.length);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(lastResults);

        // Update analytics panel with refreshed data
        if (onAnalytics) {
          onAnalytics({
            query: lastAnalyticsQuery,
            summary: summary
          });
        }
      }
    }

    refreshAnalytics();
  }, [lastResults, analyticsActive, lastAnalyticsQuery, onAnalytics, onClearAnalytics]);

  // Handle query submission
  const handleSubmit = async (e, customQuery = null) => {
    if (e) e.preventDefault();

    const query = customQuery || input;
    if (!query.trim()) return;

    setShowExamples(false);
    setInput('');
    setLoading(true);

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Check if this is a symbology/color change request
      if (conversationService.isSymbologyChangeQuery(query)) {
        const symbologyChange = conversationService.parseSymbologyChange(query);
        if (symbologyChange && onSymbologyChange) {
          console.log('🎨 Symbology change detected:', symbologyChange);
          onSymbologyChange(symbologyChange);

          // Add confirmation message
          const targetLabel = symbologyChange.target === 'wellStatus' ? 'well status' :
                              symbologyChange.target === 'tractType' ? 'tract type' :
                              symbologyChange.target === 'allWells' ? 'all wells' :
                              symbologyChange.target === 'allTracts' ? 'all tracts' : 'features';
          const confirmMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: `🎨 Updated map symbology! Changed ${targetLabel} "${symbologyChange.value}" to color ${symbologyChange.color}. The map will reflect this change.`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, confirmMessage]);
          setLoading(false);
          return;
        }
      }

      // Check if this is a filter query on existing results
      const isFilter = conversationService.isFilterQuery(query);
      const hasResults = lastResults && lastResults.length > 0;

      console.log('🔍 Filter check:', {
        query,
        isFilter,
        hasResults,
        lastResultsCount: lastResults?.length || 0
      });

      if (isFilter && hasResults) {
        console.log('✅ Filter query detected. Filtering existing results...');

        // Filter existing results
        const filteredResults = conversationService.filterResults(lastResults, query);
        console.log(`📊 Filtered ${lastResults.length} → ${filteredResults.length} results`);

        // Add assistant thinking message
        const thinkingMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: `Filtering current results based on: "${query}"`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, thinkingMessage]);

        // Display filtered results
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Filtered to ${filteredResults.length} feature${filteredResults.length !== 1 ? 's' : ''} (from ${lastResults.length} total).`,
          results: {
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Update stored results (both state and ref for immediate access)
        lastResultsRef.current = filteredResults;
        setLastResults(filteredResults);
        conversationService.storeQuery({ layer: 'tpl-tracts', description: query }, filteredResults);

        // Determine if this filter should trigger a zoom
        // Zoom when: county filter, operator filter, or significant result reduction
        const isCountyFilter = /\b(county|culberson|reeves|howard|loving|martin|midland|ector|ward|winkler|andrews|pecos|crane|glasscock|reagan|lea|eddy|chaves)\b/i.test(query);
        const isOperatorFilter = /\b(diamondback|eog|mewbourne|pioneer|chevron|exxon|bp|shell|apache|devon|occidental|ovintiv|conoco|conocophillips|coterra|marathon|cimarex|centennial|parsley|callon|energen|fasken|henry|encana|anadarko|noble|laredo|matador|highpeak|endeavor|summit|grenadier|birch)\b/i.test(query);
        const isSignificantReduction = filteredResults.length > 0 && filteredResults.length < lastResults.length * 0.8;
        const shouldZoom = isCountyFilter || isOperatorFilter || isSignificantReduction;

        // Calculate extent from filtered results for proper zooming
        let extent = null;
        if (shouldZoom && filteredResults.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          filteredResults.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            const type = feature.geometry?.type;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Pass filtered results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query,
            zoom: shouldZoom, // Zoom on county filter or significant reduction
            extent: extent // Include extent for proper zoom bounds
          });
        }

        // Smart Analyst: Generate insights for filtered results
        if (filteredResults.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(filteredResults, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return;
      }

      // Step 1: Translate natural language to GIS query
      const intent = await translateQuery(query, lastResults);

      // Add assistant thinking message
      const thinkingMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand you want to: ${intent.description}`,
        intent: intent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, thinkingMessage]);

      // Check if this is an analytics query
      if (intent.layer === 'analytics') {
        // Handle analytics query - don't query the map
        // Use ref for immediate access (state may not have updated yet in sequential queries)
        const currentResults = lastResultsRef.current || lastResults;
        console.log('Analytics query detected. currentResults:', currentResults?.length);
        if (!currentResults || currentResults.length === 0) {
          throw new Error('Please run a search first to analyze the results.');
        }

        // Generate analytics response
        const analyticsText = conversationService.generateAnalytics(query, currentResults);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(currentResults);

        const analyticsMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: analyticsText,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, analyticsMessage]);

        // Enable analytics mode and store query for auto-refresh
        setAnalyticsActive(true);
        setLastAnalyticsQuery(query);

        // Pass analytics data to parent for display in AnalyticsPanel
        if (onAnalytics) {
          console.log('ChatInterface calling onAnalytics with:', { query, summary });
          onAnalytics({
            query: query,
            summary: summary
          });
        } else {
          console.warn('onAnalytics callback not provided!');
        }

        return; // Don't execute GIS query or update map
      }

      // Check if this is a contract-based query
      if (intent.layer === 'contracts') {
        console.log('Contract query detected:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

        // Build query params from contractFilters
        const params = new URLSearchParams();
        if (intent.contractFilters.minRevenue) {
          params.append('minRevenue', intent.contractFilters.minRevenue);
        }
        if (intent.contractFilters.maxRevenue) {
          params.append('maxRevenue', intent.contractFilters.maxRevenue);
        }
        if (intent.contractFilters.status) {
          params.append('status', intent.contractFilters.status);
        }
        if (intent.contractFilters.contractType) {
          params.append('contractType', intent.contractFilters.contractType);
        }
        if (intent.contractFilters.operator) {
          params.append('operator', intent.contractFilters.operator);
        }

        // Step 1: Get tract numbers from contracts
        const contractResponse = await authFetch(`${backendUrl}/api/contracts/tract-numbers?${params}`);
        if (!contractResponse.ok) {
          throw new Error('Failed to fetch contract data');
        }
        const contractData = await contractResponse.json();
        const tractNumbers = contractData.data;

        if (tractNumbers.length === 0) {
          throw new Error('No tracts found matching those contract criteria.');
        }

        console.log(`Found ${tractNumbers.length} tracts from contract query`);

        // Step 2: Query GIS layer with those tract numbers
        const gisQuery = {
          layer: 'tracts',
          conditions: [{
            field: 'TRACT_NUMBER',
            operator: 'IN',
            value: tractNumbers,
            type: 'string'
          }],
          description: intent.description,
          zoom: false
        };

        // Execute GIS query to get tract geometries
        const results = await executeGISQuery(gisQuery);

        // Store results for follow-up queries
        conversationService.storeQuery(gisQuery, results.features);

        // Send results to map
        if (onQueryResults) {
          onQueryResults({
            features: results.features,
            count: results.count,
            layer: 'tracts',
            zoom: false
          });
        }

        // Create result message
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${results.count}** tract${results.count !== 1 ? 's' : ''} matching contract criteria.`,
          resultCount: results.count,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Store results for analytics (both state and ref)
        lastResultsRef.current = results.features;
        setLastResults(results.features);

        return; // Don't continue to normal query flow
      }

      // Check if this is a proximity query
      if (intent.layer === 'proximity') {
        console.log('Proximity query detected:', intent);

        // Load tract data from backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';
        const tractDataResponse = await authFetch(`${backendUrl}/api/mock/tracts/query?where=1=1&f=json`);

        if (!tractDataResponse.ok) {
          throw new Error('Failed to load tract data');
        }

        const tractData = await tractDataResponse.json();
        const allFeatures = tractData.features || [];

        // Determine if single-center or multi-center query
        const isMultiCenter = intent.centerTracts && Array.isArray(intent.centerTracts);
        let proximityResults;

        if (isMultiCenter) {
          // Multi-center proximity search
          proximityResults = findNearbyTractsMultiCenter(
            intent.centerTracts,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          console.log('Multi-center proximity results:', proximityResults);
        } else {
          // Single-center proximity search
          proximityResults = findNearbyTracts(
            intent.centerTract,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          // Normalize to array format for consistent handling
          proximityResults.centerTracts = [proximityResults.centerTract];
          console.log('Single-center proximity results:', proximityResults);
        }

        // Apply attribute filters if conditions are provided (combined proximity + attribute query)
        let filteredNearbyTracts = proximityResults.nearbyTracts;

        if (intent.conditions && intent.conditions.length > 0) {
          filteredNearbyTracts = proximityResults.nearbyTracts.filter(tractData => {
            const feature = tractData.feature;
            const props = feature.attributes || feature.properties || {};

            // Check all conditions (AND logic)
            return intent.conditions.every(condition => {
              const fieldValue = props[condition.field];
              const condValue = condition.value;

              // Handle different operators
              switch (condition.operator) {
                case '=':
                  return String(fieldValue).toLowerCase() === String(condValue).toLowerCase();
                case 'LIKE':
                  return String(fieldValue).toLowerCase().includes(String(condValue).replace(/%/g, '').toLowerCase());
                case '>':
                  return parseFloat(fieldValue) > parseFloat(condValue);
                case '<':
                  return parseFloat(fieldValue) < parseFloat(condValue);
                case '>=':
                  return parseFloat(fieldValue) >= parseFloat(condValue);
                case '<=':
                  return parseFloat(fieldValue) <= parseFloat(condValue);
                case 'IN':
                  const values = Array.isArray(condValue) ? condValue : [condValue];
                  return values.some(v => String(fieldValue).toLowerCase() === String(v).toLowerCase());
                case 'BETWEEN':
                  const rangeValues = Array.isArray(condValue) ? condValue : [condValue, condValue];
                  const numValue = parseFloat(fieldValue);
                  return numValue >= parseFloat(rangeValues[0]) && numValue <= parseFloat(rangeValues[1]);
                default:
                  return true;
              }
            });
          });
        }

        // Create result message with filter info if applicable
        const centerDescription = isMultiCenter
          ? `tracts ${intent.centerTracts.join(', ')}`
          : `tract ${intent.centerTract}`;

        const proximityMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: intent.conditions && intent.conditions.length > 0
            ? `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} matching criteria within ${intent.radius} ${intent.unit} of ${centerDescription} (${proximityResults.totalFound} total nearby).`
            : `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} within ${intent.radius} ${intent.unit} of ${centerDescription}.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, proximityMessage]);

        // Store proximity results for follow-up queries (including analytics)
        const proximityFeatures = filteredNearbyTracts.map(t => t.feature);
        lastResultsRef.current = proximityFeatures;
        setLastResults(proximityFeatures);
        conversationService.storeQuery(intent, proximityFeatures);

        // Pass proximity results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: proximityFeatures,
            count: filteredNearbyTracts.length,
            layer: 'proximity',
            zoom: true,
            proximityData: {
              centerTracts: proximityResults.centerTracts, // Array of center tracts
              radius: intent.radius,
              unit: intent.unit || 'miles',
              nearbyTracts: filteredNearbyTracts, // Use filtered results for map display
              isMultiCenter: isMultiCenter
            }
          });
        }

        // Smart Analyst: Generate insights for proximity results
        if (proximityFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(proximityFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Check if this is a multi-layer query (WOW FEATURE!)
      if (intent.layer === 'multi' && intent.layers && intent.layers.length > 0) {
        console.log('🌟 Multi-layer query detected:', intent.layers);

        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';
        const allFeatures = [];
        const layerResults = {};

        // Field mapping for different layers (county field varies by layer)
        const fieldMapping = {
          tracts: { County: 'CountyName' },
          wells: { CountyName: 'County' },
          contracts: { CountyName: 'County' }
        };

        // Query each layer in parallel
        const layerPromises = intent.layers.map(async (layerKey) => {
          try {
            // Adjust conditions for layer-specific field names
            const adjustedConditions = intent.conditions?.map(cond => {
              const mapping = fieldMapping[layerKey];
              if (mapping && mapping[cond.field]) {
                return { ...cond, field: mapping[cond.field] };
              }
              return cond;
            }) || [];

            // Build WHERE clause
            const whereParts = adjustedConditions.map(cond => {
              if (cond.type === 'string') {
                if (cond.operator === 'LIKE') {
                  return `${cond.field} ${cond.operator} '${cond.value}'`;
                }
                return `${cond.field} ${cond.operator} '${cond.value}'`;
              }
              return `${cond.field} ${cond.operator} ${cond.value}`;
            });
            const whereClause = whereParts.length > 0 ? whereParts.join(' AND ') : '1=1';

            const url = `${backendUrl}/api/geojson/${layerKey}?where=${encodeURIComponent(whereClause)}`;
            console.log(`📍 Querying ${layerKey}:`, url);

            const response = await authFetch(url);
            if (!response.ok) {
              console.warn(`Failed to query ${layerKey}:`, response.status);
              return { layerKey, features: [], count: 0 };
            }

            const data = await response.json();
            const features = (data.features || []).map(f => ({
              ...f,
              _sourceLayer: layerKey // Tag each feature with its source layer
            }));

            return { layerKey, features, count: features.length };
          } catch (err) {
            console.warn(`Error querying ${layerKey}:`, err);
            return { layerKey, features: [], count: 0 };
          }
        });

        const results = await Promise.all(layerPromises);

        // Combine all results
        let totalCount = 0;
        results.forEach(({ layerKey, features, count }) => {
          layerResults[layerKey] = { features, count };
          allFeatures.push(...features);
          totalCount += count;
        });

        console.log('🌟 Multi-layer results:', {
          totalCount,
          byLayer: Object.fromEntries(Object.entries(layerResults).map(([k, v]) => [k, v.count]))
        });

        // Create result message showing breakdown by layer
        const layerBreakdown = results
          .filter(r => r.count > 0)
          .map(r => `${r.count} ${r.layerKey}`)
          .join(', ');

        const multiResultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${totalCount}** features across ${intent.layers.length} layers: ${layerBreakdown}`,
          results: {
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, multiResultMessage]);

        // Calculate extent from all features
        let extent = null;
        if (allFeatures.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          allFeatures.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Store results
        lastResultsRef.current = allFeatures;
        setLastResults(allFeatures);
        conversationService.storeQuery(intent, allFeatures);

        // Pass multi-layer results to map
        if (onQueryResults) {
          onQueryResults({
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults,
            zoom: intent.zoom !== false,
            extent: extent,
            isMultiLayer: true
          });
        }

        // Smart Analyst for multi-layer results
        if (allFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(allFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Handle rigs layer queries - query the rigs GeoJSON endpoint
      if (intent.layer === 'rigs') {
        console.log('🛠️ Rigs query detected:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

        // Build WHERE clause from conditions
        // Always filter to Permian Basin (TPL's operating area)
        const permianFilter = "ENVBasin IN ('DELAWARE', 'MIDLAND', 'PERMIAN OTHER')";
        let whereClause = permianFilter;

        if (intent.conditions && intent.conditions.length > 0) {
          const clauses = intent.conditions.map(cond => {
            if (cond.operator === 'LIKE') {
              return `${cond.field} LIKE '${cond.value}'`;
            } else if (cond.operator === '=') {
              return `${cond.field} = '${cond.value}'`;
            } else if (cond.operator === '>=') {
              // Handle temporal DATE_SUBTRACT placeholders
              let value = cond.value;
              if (typeof value === 'string' && value.startsWith('DATE_SUBTRACT')) {
                const months = parseInt(value.match(/\d+/)?.[0] || '3');
                const date = new Date();
                date.setMonth(date.getMonth() - months);
                value = date.toISOString().split('T')[0];
              }
              return `${cond.field} >= '${value}'`;
            }
            return `${cond.field} ${cond.operator} '${cond.value}'`;
          });
          whereClause = permianFilter + ' AND ' + clauses.join(' AND ');
        }

        console.log('🛠️ Rigs WHERE clause:', whereClause);
        const rigsResponse = await authFetch(`${backendUrl}/api/geojson/rigs?where=${encodeURIComponent(whereClause)}`);
        const rigsData = await rigsResponse.json();

        const features = rigsData.features || [];
        lastResultsRef.current = features;
        setLastResults(features);

        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${features.length}** active drilling rigs in the Permian Basin.`,
          results: { features, count: features.length, layer: 'rigs' },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        if (onQueryResults) {
          onQueryResults({
            features,
            count: features.length,
            layer: 'rigs',
            zoom: intent.zoom !== false
          });
        }

        setLoading(false);
        return;
      }

      // Handle spatial radius queries (within X miles of county)
      if (intent.layer === 'spatial' && intent.analysisType === 'radius') {
        console.log('📍 Spatial radius query detected:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

        // County AND city center coordinates (approximate)
        const locationCoords = {
          // Counties
          'REEVES': { lat: 31.3212, lon: -103.6920 },
          'MIDLAND': { lat: 31.9974, lon: -102.0779 },
          'HOWARD': { lat: 32.3072, lon: -101.4549 },
          'MARTIN': { lat: 32.3058, lon: -101.9500 },
          'LOVING': { lat: 31.8493, lon: -103.9785 },
          'WARD': { lat: 31.5097, lon: -103.1018 },
          'ECTOR': { lat: 31.8659, lon: -102.5417 },
          'PECOS': { lat: 30.7867, lon: -103.1389 },
          'CULBERSON': { lat: 31.4471, lon: -104.5178 },
          'ANDREWS': { lat: 32.3131, lon: -102.5451 },
          'CRANE': { lat: 31.4222, lon: -102.3518 },
          'GLASSCOCK': { lat: 31.8697, lon: -101.5200 },
          'REAGAN': { lat: 31.3661, lon: -101.4920 },
          'WINKLER': { lat: 31.8493, lon: -103.0557 },
          'LEA': { lat: 32.6800, lon: -103.4200 },
          'EDDY': { lat: 32.4000, lon: -104.2300 },
          // Major cities in Permian Basin
          'ODESSA': { lat: 31.8457, lon: -102.3676 },
          'MIDLAND CITY': { lat: 31.9974, lon: -102.0779 },
          'BIG SPRING': { lat: 32.2504, lon: -101.4785 },
          'PECOS CITY': { lat: 31.4229, lon: -103.4932 },
          'MONAHANS': { lat: 31.5943, lon: -102.8927 },
          'KERMIT': { lat: 31.8576, lon: -103.0929 },
          'FORT STOCKTON': { lat: 30.8941, lon: -102.8793 },
          'ALPINE': { lat: 30.3585, lon: -103.6610 },
          'CARLSBAD': { lat: 32.4207, lon: -104.2288 },
          'HOBBS': { lat: 32.7026, lon: -103.1360 },
          'ARTESIA': { lat: 32.8423, lon: -104.4036 }
        };

        // Try exact match first, then try with common suffixes removed
        let lookupKey = intent.centerCounty?.toUpperCase();
        let center = locationCoords[lookupKey];

        // If not found, try stripping "COUNTY" or "CITY" suffix
        if (!center && lookupKey) {
          const stripped = lookupKey.replace(/\s*(COUNTY|CITY)$/i, '').trim();
          center = locationCoords[stripped];
        }

        if (!center) {
          const availableLocations = Object.keys(locationCoords).slice(0, 10).join(', ');
          const errorMsg = {
            id: Date.now() + 2,
            type: 'error',
            content: `Unknown location: "${intent.centerCounty}". Try: ${availableLocations}...`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMsg]);
          setLoading(false);
          return;
        }

        // Convert radius to degrees (rough approximation: 1 degree ≈ 69 miles)
        const radiusMiles = intent.unit === 'km' ? intent.radius * 0.621371 : intent.radius;
        const radiusDegrees = radiusMiles / 69;

        // Query the target layer with bounding box
        const targetLayer = intent.targetLayer || 'wells';
        const response = await authFetch(`${backendUrl}/api/geojson/${targetLayer}`);
        const data = await response.json();

        // Filter by distance (simple bounding box + Haversine for accuracy)
        const filteredFeatures = (data.features || []).filter(f => {
          const geom = f.geometry;
          let lat, lon;
          if (geom.type === 'Point') {
            [lon, lat] = geom.coordinates;
          } else if (geom.type === 'LineString') {
            [lon, lat] = geom.coordinates[0];
          } else if (f.properties.RigLatitude && f.properties.RigLongitude) {
            lat = f.properties.RigLatitude;
            lon = f.properties.RigLongitude;
          } else {
            return false;
          }

          // Haversine distance calculation
          const R = 3959; // Earth radius in miles
          const dLat = (lat - center.lat) * Math.PI / 180;
          const dLon = (lon - center.lon) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(center.lat * Math.PI/180) * Math.cos(lat * Math.PI/180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          const distance = R * c;

          return distance <= radiusMiles;
        });

        lastResultsRef.current = filteredFeatures;
        setLastResults(filteredFeatures);

        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${filteredFeatures.length}** ${targetLayer} within ${intent.radius} ${intent.unit} of ${intent.centerCounty} County.`,
          results: { features: filteredFeatures, count: filteredFeatures.length, layer: targetLayer },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        if (onQueryResults) {
          onQueryResults({
            features: filteredFeatures,
            count: filteredFeatures.length,
            layer: targetLayer,
            zoom: true
          });
        }

        setLoading(false);
        return;
      }

      // Handle synthetic data generation commands
      if (intent.layer === 'synthetic') {
        console.log('🧪 Synthetic data command:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

        if (intent.action === 'generate') {
          const response = await authFetch(`${backendUrl}/api/snapshots/synthetic/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              operator: intent.operator,
              count: intent.count || 5,
              status: intent.status || 'PERMITTED',
              county: intent.county
            })
          });
          const result = await response.json();

          const msg = result.success
            ? `✅ Generated **${result.generated}** synthetic ${intent.status || 'PERMITTED'} wells for ${intent.operator}. Run your query again to see them on the map!`
            : `❌ Failed to generate wells: ${result.error}`;

          setMessages(prev => [...prev, { id: Date.now() + 2, type: result.success ? 'result' : 'error', content: msg, timestamp: new Date() }]);
        } else if (intent.action === 'update_status') {
          const response = await authFetch(`${backendUrl}/api/snapshots/synthetic/update-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fromStatus: intent.fromStatus,
              toStatus: intent.toStatus,
              count: intent.count || 5
            })
          });
          const result = await response.json();

          const msg = result.success
            ? `✅ Progressed **${result.updated}** wells from ${intent.fromStatus} to ${intent.toStatus}.`
            : `❌ ${result.message || 'Failed to update status'}`;

          setMessages(prev => [...prev, { id: Date.now() + 2, type: result.success ? 'result' : 'error', content: msg, timestamp: new Date() }]);
        } else if (intent.action === 'clear') {
          const response = await authFetch(`${backendUrl}/api/snapshots/synthetic`, { method: 'DELETE' });
          const result = await response.json();

          setMessages(prev => [...prev, { id: Date.now() + 2, type: 'result', content: `✅ Cleared ${result.deleted || 0} synthetic wells.`, timestamp: new Date() }]);
        }

        setLoading(false);
        return;
      }

      // Handle saved analysis commands via chat
      if (intent.layer === 'savedAnalysis') {
        console.log('💾 Saved analysis command:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

        if (intent.action === 'save') {
          // Save current results to backend
          if (!currentResults || currentResults.length === 0) {
            throw new Error('No results to save. Please run a query first.');
          }

          const snapshotData = {
            name: intent.name || 'Unnamed Analysis',
            description: intent.description || `Saved from chat: ${queryText}`,
            queryText: queryText,
            featureCount: currentResults.length,
            features: currentResults,
            analyticsData: analyticsData,
            layer: 'savedAnalysis'
          };

          const response = await authFetch(`${backendUrl}/api/snapshots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(snapshotData)
          });

          if (!response.ok) {
            throw new Error('Failed to save analysis');
          }

          const result = await response.json();
          const savedVersion = result.data;

          const msg = `✅ Saved "${savedVersion.name}" (v${savedVersion.version}) with ${savedVersion.feature_count} features. Use "Show my saved analyses" to see all saved analyses.`;
          setMessages(prev => [...prev, { id: Date.now() + 2, type: 'result', content: msg, timestamp: new Date() }]);

        } else if (intent.action === 'list') {
          // List all saved analysis series
          const response = await authFetch(`${backendUrl}/api/snapshots/series`);
          if (!response.ok) {
            throw new Error('Failed to fetch saved analyses');
          }

          const result = await response.json();
          const series = result.data || [];

          if (series.length === 0) {
            setMessages(prev => [...prev, {
              id: Date.now() + 2,
              type: 'result',
              content: '📂 No saved analyses yet. Run a query and say "Save this as [name]" to create one.',
              timestamp: new Date()
            }]);
          } else {
            // Display as a nice list in chat
            const listItems = series.map(s =>
              `• **${s.base_name}** - ${s.version_count} version${s.version_count !== 1 ? 's' : ''}, last updated: ${new Date(s.last_updated).toLocaleDateString()}`
            ).join('\n');

            setMessages(prev => [...prev, {
              id: Date.now() + 2,
              type: 'saved-analyses-list',
              content: `📂 **Saved Analyses (${series.length}):**\n\n${listItems}`,
              series: series,
              timestamp: new Date()
            }]);
          }

        } else if (intent.action === 'load') {
          // Load a saved analysis by name
          const baseName = intent.name || intent.baseName;
          if (!baseName) {
            throw new Error('Please specify which analysis to load (e.g., "Load Reeves Q4 analysis")');
          }

          // First get versions for this base name
          const versionsResponse = await authFetch(`${backendUrl}/api/snapshots/versions/${encodeURIComponent(baseName)}`);
          if (!versionsResponse.ok) {
            throw new Error(`Analysis "${baseName}" not found`);
          }

          const versionsResult = await versionsResponse.json();
          const versions = versionsResult.data || [];

          if (versions.length === 0) {
            throw new Error(`No versions found for "${baseName}"`);
          }

          // Load the latest version (or specific version if requested)
          const targetVersion = intent.version ?
            versions.find(v => v.version === intent.version) :
            versions[0]; // Latest is first

          if (!targetVersion) {
            throw new Error(`Version ${intent.version} not found for "${baseName}"`);
          }

          // Fetch full snapshot data
          const snapshotResponse = await authFetch(`${backendUrl}/api/snapshots/${targetVersion.id}`);
          if (!snapshotResponse.ok) {
            throw new Error('Failed to load analysis data');
          }

          const snapshotResult = await snapshotResponse.json();
          const snapshot = snapshotResult.data;

          // Load features onto map
          if (snapshot.features && snapshot.features.length > 0 && onQueryResults) {
            onQueryResults({
              features: snapshot.features,
              count: snapshot.features.length,
              layer: 'tracts',
              query: snapshot.query_text,
              zoom: true
            });

            // Update local results
            lastResultsRef.current = snapshot.features;
            setLastResults(snapshot.features);
          }

          const msg = `✅ Loaded **${snapshot.name}** (v${snapshot.version}) with ${snapshot.feature_count} features.\n\nOriginal query: "${snapshot.query_text || 'N/A'}"`;
          setMessages(prev => [...prev, { id: Date.now() + 2, type: 'result', content: msg, timestamp: new Date() }]);

        } else if (intent.action === 'compare') {
          // Compare two versions
          const baseName = intent.baseName || intent.name;
          if (!baseName) {
            throw new Error('Please specify which analysis to compare (e.g., "Compare Reeves Q4 v1 vs v2")');
          }

          // Get versions for this analysis
          const versionsResponse = await authFetch(`${backendUrl}/api/snapshots/versions/${encodeURIComponent(baseName)}`);
          if (!versionsResponse.ok) {
            throw new Error(`Analysis "${baseName}" not found`);
          }

          const versionsResult = await versionsResponse.json();
          const versions = versionsResult.data || [];

          if (versions.length < 2) {
            throw new Error(`"${baseName}" only has ${versions.length} version(s). Need at least 2 to compare.`);
          }

          // Find the two versions to compare
          const v1 = intent.version1 ? versions.find(v => v.version === intent.version1) : versions[1]; // Older
          const v2 = intent.version2 ? versions.find(v => v.version === intent.version2) : versions[0]; // Newer

          if (!v1 || !v2) {
            throw new Error('Could not find the specified versions to compare');
          }

          // Call compare API
          const compareResponse = await authFetch(`${backendUrl}/api/snapshots/compare/${v1.id}/${v2.id}`);
          if (!compareResponse.ok) {
            throw new Error('Failed to compare versions');
          }

          const compareResult = await compareResponse.json();
          const comparison = compareResult.data;

          // Format comparison results for chat
          const delta = comparison.current.feature_count - comparison.baseline.feature_count;
          const deltaSign = delta >= 0 ? '+' : '';
          const deltaColor = delta >= 0 ? '📈' : '📉';

          let comparisonMsg = `📊 **Comparison: ${baseName}**\n\n`;
          comparisonMsg += `**v${comparison.baseline.version}** → **v${comparison.current.version}**\n\n`;
          comparisonMsg += `${deltaColor} Feature count: ${comparison.baseline.feature_count} → ${comparison.current.feature_count} (${deltaSign}${delta})\n\n`;

          if (comparison.changes) {
            if (comparison.changes.added?.length > 0) {
              comparisonMsg += `✅ **Added (${comparison.changes.added.length}):** ${comparison.changes.added.slice(0, 5).join(', ')}${comparison.changes.added.length > 5 ? '...' : ''}\n`;
            }
            if (comparison.changes.removed?.length > 0) {
              comparisonMsg += `❌ **Removed (${comparison.changes.removed.length}):** ${comparison.changes.removed.slice(0, 5).join(', ')}${comparison.changes.removed.length > 5 ? '...' : ''}\n`;
            }
          }

          setMessages(prev => [...prev, {
            id: Date.now() + 2,
            type: 'comparison-result',
            content: comparisonMsg,
            comparison: comparison,
            timestamp: new Date()
          }]);

        } else if (intent.action === 'export') {
          // Export analysis to JSON file
          const baseName = intent.name || intent.baseName;
          if (!baseName) {
            throw new Error('Please specify which analysis to export (e.g., "Export Reeves Q4 analysis")');
          }

          // Get versions and find the latest
          const versionsResponse = await authFetch(`${backendUrl}/api/snapshots/versions/${encodeURIComponent(baseName)}`);
          if (!versionsResponse.ok) {
            throw new Error(`Analysis "${baseName}" not found`);
          }

          const versionsResult = await versionsResponse.json();
          const versions = versionsResult.data || [];

          if (versions.length === 0) {
            throw new Error(`No versions found for "${baseName}"`);
          }

          const targetVersion = versions[0]; // Latest

          // Fetch export data
          const exportResponse = await authFetch(`${backendUrl}/api/snapshots/export/${targetVersion.id}`);
          if (!exportResponse.ok) {
            throw new Error('Failed to export analysis');
          }

          const exportData = await exportResponse.json();

          // Trigger download
          const blob = new Blob([JSON.stringify(exportData.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseName.replace(/\s+/g, '_')}_v${targetVersion.version}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          setMessages(prev => [...prev, {
            id: Date.now() + 2,
            type: 'result',
            content: `📥 Exported "${baseName}" v${targetVersion.version} to JSON file.`,
            timestamp: new Date()
          }]);

        } else if (intent.action === 'delete') {
          // Delete an analysis
          const baseName = intent.name || intent.baseName;
          if (!baseName) {
            throw new Error('Please specify which analysis to delete (e.g., "Delete Reeves Q4 analysis")');
          }

          // Get versions to delete
          const versionsResponse = await authFetch(`${backendUrl}/api/snapshots/versions/${encodeURIComponent(baseName)}`);
          if (!versionsResponse.ok) {
            throw new Error(`Analysis "${baseName}" not found`);
          }

          const versionsResult = await versionsResponse.json();
          const versions = versionsResult.data || [];

          if (versions.length === 0) {
            throw new Error(`No versions found for "${baseName}"`);
          }

          // Delete all versions
          let deletedCount = 0;
          for (const version of versions) {
            const deleteResponse = await authFetch(`${backendUrl}/api/snapshots/${version.id}`, { method: 'DELETE' });
            if (deleteResponse.ok) deletedCount++;
          }

          setMessages(prev => [...prev, {
            id: Date.now() + 2,
            type: 'result',
            content: `🗑️ Deleted "${baseName}" (${deletedCount} version${deletedCount !== 1 ? 's' : ''}).`,
            timestamp: new Date()
          }]);
        }

        setLoading(false);
        return;
      }

      // Step 2: Execute GIS query (for spatial queries)
      const results = await executeGISQuery(intent);

      // Store results for follow-up queries (both ref for immediate access and state)
      console.log('Storing results. features count:', results.features?.length);
      lastResultsRef.current = results.features;
      setLastResults(results.features);
      conversationService.storeQuery(intent, results.features);
      console.log('Results stored in ref (immediate) and state.');

      // Step 3: Display results
      const resultMessage = {
        id: Date.now() + 2,
        type: 'result',
        content: `Found ${results.count} feature${results.count !== 1 ? 's' : ''} matching your query.`,
        results: results,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, resultMessage]);

      // Pass results to parent (map component)
      if (onQueryResults) {
        onQueryResults(results);
      }

      // Smart Analyst: Generate proactive insights and suggestions
      if (results.features && results.features.length > 0) {
        const smartInsights = conversationService.generateSmartInsights(results.features, query);
        if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
          const insightsMessage = {
            id: Date.now() + 3,
            type: 'smart-analyst',
            insights: smartInsights.insights,
            suggestions: smartInsights.suggestions,
            summary: smartInsights.summary,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, insightsMessage]);
        }
      }

    } catch (error) {
      console.error('Query failed:', error);

      const errorMessage = {
        id: Date.now() + 3,
        type: 'error',
        content: error.message || 'Sorry, I couldn\'t process that query. Please try rephrasing.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle example query click
  const handleExampleClick = (exampleQuery) => {
    setInput(exampleQuery);
    handleSubmit(null, exampleQuery);
  };

  // Extract unique values from current results for dynamic filtering
  const getUniqueValuesFromResults = (fieldNames) => {
    if (!lastResults || lastResults.length === 0) return [];

    const values = new Set();
    lastResults.forEach(feature => {
      const props = feature.properties || feature.attributes || {};
      for (const fieldName of fieldNames) {
        const value = props[fieldName];
        if (value && value.toString().trim()) {
          values.add(value.toString().trim());
        }
      }
    });

    // Return sorted, limited to top 8
    return Array.from(values).sort().slice(0, 8);
  };

  // Check if current results have well-specific fields (vs tracts)
  const hasWellsData = () => {
    if (!lastResults || lastResults.length === 0) return true; // Default: show all options
    const sample = lastResults[0];
    const props = sample?.properties || sample?.attributes || {};
    // Wells have ENVOperator, ENVWellStatus, ENVInterval; tracts don't
    return !!(props.ENVOperator || props.Operator || props.ENVWellStatus || props.WellStatus);
  };

  // Filter smart queries based on current data type
  const getApplicableSmartQueries = () => {
    const isWellsData = hasWellsData();
    return smartQueries.filter(sq => {
      // These filters only work on wells data
      const wellsOnlyFilters = ['filter-operator', 'filter-status', 'filter-formation'];
      if (wellsOnlyFilters.includes(sq.id) && !isWellsData) {
        return false;
      }
      return true;
    });
  };

  // Handle smart query click - shows follow-up question with chips
  const handleSmartQueryClick = (smartQuery) => {
    // Build dynamic options based on current results
    let dynamicOptions = smartQuery.options; // fallback to defaults

    if (lastResults && lastResults.length > 0) {
      switch (smartQuery.id) {
        case 'filter-county':
          dynamicOptions = getUniqueValuesFromResults(['CountyName', 'County', 'COUNTY']);
          break;
        case 'filter-operator':
          dynamicOptions = getUniqueValuesFromResults(['ENVOperator', 'Operator', 'OPERATOR']);
          break;
        case 'filter-status':
          dynamicOptions = getUniqueValuesFromResults(['ENVWellStatus', 'WellStatus', 'Status']);
          break;
        case 'filter-formation':
          dynamicOptions = getUniqueValuesFromResults(['ENVInterval', 'Formation', 'Interval']);
          break;
        case 'filter-contract':
          dynamicOptions = getUniqueValuesFromResults(['ContractType', 'Type']);
          break;
        case 'filter-acreage':
          // For acreage, keep static options as they're ranges
          dynamicOptions = smartQuery.options;
          break;
        default:
          dynamicOptions = smartQuery.options;
      }
    }

    // If no values found, use defaults
    if (!dynamicOptions || dynamicOptions.length === 0) {
      dynamicOptions = smartQuery.options;
    }

    // Add assistant message asking the question
    const questionMessage = {
      id: Date.now(),
      type: 'assistant',
      content: smartQuery.question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, questionMessage]);
    setShowExamples(false);

    // Set pending interaction with dynamic options
    setPendingInteraction({
      ...smartQuery,
      options: dynamicOptions
    });
  };

  // Handle chip selection from smart query
  const handleChipSelect = (value) => {
    if (!pendingInteraction) return;

    // Build the actual query from template
    const query = pendingInteraction.template.replace('{value}', value);

    // Clear pending interaction
    setPendingInteraction(null);

    // Execute the query
    setInput(query);
    handleSubmit(null, query);
  };

  // Cancel pending interaction
  const handleCancelInteraction = () => {
    setPendingInteraction(null);
    // Remove the question message
    setMessages(prev => prev.slice(0, -1));
  };

  // Show examples without clearing results
  const handleShowExamples = () => {
    setShowExamples(true);
  };

  // Clear conversation
  const handleClear = () => {
    setMessages([]);
    setShowExamples(true);
    setInput('');
    lastResultsRef.current = null;
    setLastResults(null);
    setAnalyticsActive(false);
    setLastAnalyticsQuery(null);
    setCurrentAnalyticsData(null);
    conversationService.clear();

    // Clear analytics panel
    if (onClearAnalytics) {
      onClearAnalytics();
    }

    // Clear map markers and polygons
    if (onQueryResults) {
      onQueryResults(null);
    }
  };

  // ===== SAVED ANALYSIS FUNCTIONS =====

  // Save current analysis
  const handleSaveAnalysis = async () => {
    if (!analysisName.trim()) {
      alert('Please enter a name for this analysis');
      return;
    }

    // Extract queries from messages
    const userQueries = messages
      .filter(m => m.type === 'user')
      .map(m => m.content);

    // Get last query
    const lastQuery = userQueries[userQueries.length - 1] || '';

    // Try to extract county/operator from queries
    let county = '';
    let operator = '';
    for (const q of userQueries) {
      const countyMatch = q.match(/\b(Howard|Reeves|Loving|Martin|Midland|Lea|Culberson|Ward|Pecos|Ector)\b/i);
      if (countyMatch) county = countyMatch[1];
      const opMatch = q.match(/\b(Diamondback|EOG|Occidental|Mewbourne|Devon|ConocoPhillips|Exxon)\b/i);
      if (opMatch) operator = opMatch[1];
    }

    try {
      const saved = await dossierService.saveDossier({
        name: analysisName.trim(),
        description: analysisDescription.trim(),
        queries: userQueries,
        lastQuery: lastQuery,
        layer: 'tracts',
        resultCount: lastResults?.length || 0,
        analyticsData: currentAnalyticsData,
        county: county,
        operator: operator
      });

      // Update local state
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);

      // Reset modal
      setShowSaveModal(false);
      setAnalysisName('');
      setAnalysisDescription('');

      // Show success message
      const successMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Analysis saved to database as "${saved.name}". This will persist even if you close your browser.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMsg]);

    } catch (error) {
      alert('Error saving analysis: ' + error.message);
    }
  };

  // Check if a query is an analytics/summary query (should run after data queries)
  const isAnalyticsQuery = (query) => {
    const analyticsKeywords = [
      'summarize', 'summary', 'analyze', 'analytics', 'breakdown',
      'group by', 'count by', 'total by', 'aggregate', 'statistics'
    ];
    const lowerQuery = query.toLowerCase();
    return analyticsKeywords.some(keyword => lowerQuery.includes(keyword));
  };

  // Load a saved analysis
  const handleLoadAnalysis = async (analysis) => {
    setShowLoadPanel(false);

    // Clear current state
    handleClear();

    // Re-run the queries from the saved analysis
    if (analysis.queries && analysis.queries.length > 0) {
      // Add a loading message
      const loadingMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Loading analysis "${analysis.name}"...`,
        timestamp: new Date()
      };
      setMessages([loadingMsg]);
      setShowExamples(false);

      // Separate data queries from analytics queries
      // Data queries must run first, analytics queries run last
      const dataQueries = analysis.queries.filter(q => !isAnalyticsQuery(q));
      const analyticsQueries = analysis.queries.filter(q => isAnalyticsQuery(q));

      // Reorder: data queries first, then analytics queries
      const orderedQueries = [...dataQueries, ...analyticsQueries];

      // Execute queries sequentially with delays
      // First query should zoom to results
      const executeQueriesSequentially = async (queries, index = 0) => {
        if (index >= queries.length) return;

        const query = queries[index];
        const isFirstDataQuery = index === 0 && !isAnalyticsQuery(query);

        // For the first data query, we want to zoom
        // We'll handle this by checking in handleSubmit or by modifying the query context
        console.log(`📋 Loading query ${index + 1}/${queries.length}: "${query}" (zoom: ${isFirstDataQuery})`);

        // Execute the query
        await new Promise(resolve => {
          setTimeout(async () => {
            await handleSubmit(null, query);
            resolve();
          }, index === 0 ? 500 : 1500); // First query 500ms, subsequent 1500ms to allow processing
        });

        // Continue to next query
        if (index < queries.length - 1) {
          executeQueriesSequentially(queries, index + 1);
        }
      };

      executeQueriesSequentially(orderedQueries);
    }
  };

  // Delete a saved analysis
  const handleDeleteAnalysis = async (id, name) => {
    if (window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      await dossierService.deleteDossier(id);
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
  };

  // Export a saved analysis
  const handleExportAnalysis = async (id) => {
    await dossierService.exportDossier(id);
  };

  // Import analysis from file
  const handleImportAnalysis = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const count = await dossierService.importDossier(e.target.result);
        const analyses = await dossierService.getAllDossiers();
        setSavedAnalyses(analyses);
        alert(`Successfully imported ${count} analysis${count !== 1 ? 'es' : ''}`);
      } catch (error) {
        alert('Error importing: ' + error.message);
      }
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-interface">
      <div className="chat-header chat-header-compact">
        <div className="header-buttons">
          {/* Saved Analysis buttons */}
          <button
            onClick={() => setShowLoadPanel(!showLoadPanel)}
            className="header-btn load-btn"
            title="Load Saved Analysis"
          >
            <FolderOpen size={16} />
            {savedAnalyses.length > 0 && (
              <span className="badge">{savedAnalyses.length}</span>
            )}
          </button>
          {messages.length > 0 && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="header-btn save-btn"
              title="Save Current Analysis"
            >
              <Save size={16} />
            </button>
          )}
          {messages.length > 0 && !showExamples && (
            <button
              onClick={handleShowExamples}
              className="header-btn examples-btn"
              title="Show Examples"
            >
              <Lightbulb size={16} />
            </button>
          )}
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="header-btn clear-btn"
              title="Clear Chat"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Save Analysis Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="save-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Analysis</h3>
              <button onClick={() => setShowSaveModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={analysisName}
                  onChange={e => setAnalysisName(e.target.value)}
                  placeholder="e.g., Howard County Q4 Review"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={analysisDescription}
                  onChange={e => setAnalysisDescription(e.target.value)}
                  placeholder="Notes about this analysis..."
                  rows={3}
                />
              </div>
              <div className="modal-info">
                <p><strong>Queries:</strong> {messages.filter(m => m.type === 'user').length}</p>
                <p><strong>Results:</strong> {lastResults?.length || 0} features</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSaveModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleSaveAnalysis} className="save-action-btn">
                <Save size={16} /> Save Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Analysis Modal */}
      {showLoadPanel && (
        <div className="modal-overlay" onClick={() => setShowLoadPanel(false)}>
          <div className="load-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Saved Analyses</h3>
              <div className="load-panel-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportAnalysis}
                  accept=".json"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="import-btn"
                  title="Import from file"
                >
                  <Upload size={16} />
                </button>
                <button onClick={() => setShowLoadPanel(false)} className="close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="load-modal-content">
              {savedAnalyses.length === 0 ? (
                <div className="empty-state">
                  <FolderOpen size={32} />
                  <p>No saved analyses yet</p>
                  <small>Run a query and click Save to create one</small>
                </div>
              ) : (
                <div className="saved-list">
                  {savedAnalyses.map(analysis => (
                    <div key={analysis.id} className="saved-item">
                      <div className="saved-item-main" onClick={() => handleLoadAnalysis(analysis)}>
                        <div className="saved-item-name">{analysis.name}</div>
                        <div className="saved-item-meta">
                          <span>{formatDate(analysis.createdAt)}</span>
                          {analysis.county && <span className="tag">{analysis.county}</span>}
                          {analysis.operator && <span className="tag">{analysis.operator}</span>}
                        </div>
                        {analysis.description && (
                          <div className="saved-item-desc">{analysis.description}</div>
                        )}
                        <div className="saved-item-stats">
                          <span>{analysis.queries?.length || 0} queries</span>
                          <span>{analysis.resultCount || 0} results</span>
                        </div>
                      </div>
                      <div className="saved-item-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportAnalysis(analysis.id); }}
                          title="Export"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnalysis(analysis.id, analysis.name); }}
                          title="Delete"
                          className="delete-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 && showExamples ? (
          <div className="welcome-screen">
            <h3>👋 Welcome to {tenantConfig.name}!</h3>
            <p>{tenantConfig.tagline}</p>

            <div className="examples-section">
              <div className="examples-header">
                <Lightbulb size={20} />
                <h4>Try these examples:</h4>
              </div>

              {exampleQueries.map((category, idx) => (
                <div key={idx} className="example-category">
                  <h5>{category.category}</h5>
                  {category.queries.map((query, qIdx) => (
                    <button
                      key={qIdx}
                      className="example-query"
                      onClick={() => handleExampleClick(query)}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(message => (
              <div key={message.id} className={`message message-${message.type}`}>
                {message.type === 'user' && (
                  <div className="message-content">
                    <strong>You:</strong> {message.content}
                  </div>
                )}

                {message.type === 'assistant' && (
                  <div className="message-content">
                    <strong>Assistant:</strong> {message.content}
                    {message.intent && message.intent.confidence < 0.7 && (
                      <div className="confidence-warning">
                        <AlertCircle size={16} />
                        <span>Confidence: {(message.intent.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'result' && (
                  <div className="message-content">
                    <strong>Results:</strong> <FormattedText text={message.content} />
                    {message.results && message.results.count > 0 && (
                      <div className="result-summary">
                        <p>Layer: <strong>{message.results.layer}</strong></p>
                        <p>Query: <code>{message.results.query}</code></p>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'error' && (
                  <div className="message-content error">
                    <AlertCircle size={20} />
                    <strong>Error:</strong> {message.content}
                  </div>
                )}

                {message.type === 'smart-analyst' && (
                  <div className="message-content smart-analyst">
                    <div className="smart-analyst-header">
                      <Sparkles size={18} className="sparkle-icon" />
                      <strong>Smart Analyst</strong>
                    </div>

                    {message.insights && message.insights.length > 0 && (
                      <div className="smart-insights">
                        {message.insights.map((insight, idx) => (
                          <div key={idx} className="insight-item">
                            <FormattedText text={insight} />
                          </div>
                        ))}
                      </div>
                    )}

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="smart-suggestions">
                        <div className="suggestions-header">Would you like to:</div>
                        <div className="suggestion-buttons">
                          {message.suggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className="suggestion-btn"
                              onClick={() => handleSubmit(null, suggestion.query)}
                            >
                              <ArrowRight size={14} />
                              {suggestion.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Smart Filters inside Smart Analyst - filtered by data type */}
                    <div className="smart-refine-section">
                      <div className="suggestions-header">Refine your results:</div>
                      <div className="smart-refine-buttons">
                        {getApplicableSmartQueries().map((sq) => (
                          <button
                            key={sq.id}
                            className="smart-refine-btn"
                            onClick={() => handleSmartQueryClick(sq)}
                          >
                            {sq.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Saved Analyses List - interactive with load buttons */}
                {message.type === 'saved-analyses-list' && (
                  <div className="message-content saved-analyses-list">
                    <div className="saved-analyses-header">
                      <FolderOpen size={18} />
                      <strong>Saved Analyses</strong>
                    </div>
                    {message.series && message.series.length > 0 ? (
                      <div className="saved-analyses-items">
                        {message.series.map((s, idx) => (
                          <div key={idx} className="saved-analysis-item">
                            <div className="saved-analysis-info">
                              <span className="saved-analysis-name">{s.base_name}</span>
                              <span className="saved-analysis-meta">
                                {s.version_count} version{s.version_count !== 1 ? 's' : ''} ·
                                {new Date(s.latest_date).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="saved-analysis-actions">
                              <button
                                className="load-analysis-btn"
                                onClick={() => handleSubmit(null, `Load ${s.base_name} analysis`)}
                                title="Load this analysis"
                              >
                                <ArrowRight size={14} /> Load
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-analyses">No saved analyses yet.</p>
                    )}
                  </div>
                )}

                {/* Comparison Result - visual diff display */}
                {message.type === 'comparison-result' && (
                  <div className="message-content comparison-result">
                    <FormattedText text={message.content} />
                    {message.comparison && (
                      <div className="comparison-actions">
                        <button
                          className="load-version-btn"
                          onClick={() => handleSubmit(null, `Load ${message.comparison.baseline.name} v${message.comparison.baseline.version}`)}
                        >
                          Load v{message.comparison.baseline.version}
                        </button>
                        <button
                          className="load-version-btn"
                          onClick={() => handleSubmit(null, `Load ${message.comparison.current.name} v${message.comparison.current.version}`)}
                        >
                          Load v{message.comparison.current.version}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}

            {/* Smart Query Chips - shown when user clicks a smart filter */}
            {pendingInteraction && (
              <div className="smart-chips-container">
                <div className="smart-chips-grid">
                  {pendingInteraction.options.map((option, idx) => (
                    <button
                      key={idx}
                      className="smart-chip"
                      onClick={() => handleChipSelect(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button
                  className="smart-chips-cancel"
                  onClick={handleCancelInteraction}
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            )}

            {/* Show examples after messages if requested */}
            {showExamples && messages.length > 0 && (
              <div className="examples-section" style={{ marginTop: '1rem' }}>
                <div className="examples-header">
                  <Lightbulb size={20} />
                  <h4>Try these examples:</h4>
                </div>

                {exampleQueries.map((category, idx) => (
                  <div key={idx} className="example-category">
                    <h5>{category.category}</h5>
                    {category.queries.map((query, qIdx) => (
                      <button
                        key={qIdx}
                        className="example-query"
                        onClick={() => handleExampleClick(query)}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {loading && (
          <div className="message message-loading">
            <Loader className="spinner" size={20} />
            <span>Processing your query...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="chat-input-form">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your GIS data... (e.g., 'Show me tract 12345')"
          disabled={loading}
          className="chat-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="send-button"
        >
          <Send size={20} />
        </button>
      </form>

      <div className="chat-footer">
        <small>
          Powered by CODVO AI · Natural language GIS queries
        </small>
      </div>
    </div>
  );
});

export default ChatInterface;
